import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import './shepherd-custom.css';
import { saveTourCompletion, isTourCompleted, hasRegionAccess, validateTourForPage } from './tourData';

const isMobileScreen = () => window.innerWidth <= 768;

export const useShepherdTour = (tourName, steps, autoStart = false, onComplete) => {
  const tourRef = useRef(null);
  const hasStarted = useRef(false);
  const currentPath = useRef(window.location.pathname);

  useEffect(() => {
    const initTour = async () => {
      // Validate we're still on the correct page
      if (currentPath.current !== window.location.pathname) {
        return;
      }

      if (!hasRegionAccess()) {
        console.log('Tour skipped: No region access');
        return;
      }

      // Validate tour is for current page
      if (!validateTourForPage(tourName, window.location.pathname)) {
        console.log(`Tour skipped: ${tourName} not valid for current page`);
        return;
      }

      if (tourName === 'navigation' && isMobileScreen()) {
        console.log('Navigation tour skipped: Mobile screen');
        await saveTourCompletion(tourName);
        if (onComplete) onComplete();
        return;
      }

      const completed = await isTourCompleted(tourName);
      if (completed || hasStarted.current) {
        return;
      }

      if (tourName === 'question_list_filters' && isMobileScreen()) {
    const moreFiltersBtn = document.querySelector('.more-filters-btn');
    if (moreFiltersBtn && !document.querySelector('.mobile-filters-dropdown')) {
      moreFiltersBtn.click();
      // Wait for dropdown to render
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

      // Double-check page hasn't changed during async operations
      if (currentPath.current !== window.location.pathname) {
        return;
      }

      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          cancelIcon: {
            enabled: true
          },
          classes: 'custom-shepherd-theme',
          scrollTo: { behavior: 'smooth', block: 'center' },
          modalOverlayOpeningPadding: 4,
          modalOverlayOpeningRadius: 4
        }
      });

      steps.forEach((step, index) => {
        tour.addStep({
          id: `step-${index}`,
          text: `
             <div class="shepherd-header">
    <span class="shepherd-emoji">${step.emoji}</span>
    <h3 class="shepherd-title">${step.title}</h3>
  </div>
            <p class="shepherd-description">${step.description}</p>
          `,
          attachTo: {
            element: `#${step.element}`,
            on: "auto"
          },
          buttons: [
            {
              text: 'Skip tour',
              classes: 'shepherd-button-skip',
              action: tour.cancel
            },
            {
              text: index === steps.length - 1 ? '✓' : '›',
              classes: 'shepherd-button-next',
              action: tour.next
            }
          ]
        });
      });

      tour.on('complete', async () => {
        await saveTourCompletion(tourName);
        if (onComplete) onComplete();
      });

      tour.on('cancel', async () => {
        await saveTourCompletion(tourName); 
        if (onComplete) onComplete();   
        tour.hide();
      });

      tourRef.current = tour;

      if (autoStart && currentPath.current === window.location.pathname) {
        hasStarted.current = true;
        setTimeout(() => {
          // Final validation before starting
          if (currentPath.current === window.location.pathname && validateTourForPage(tourName, window.location.pathname)) {
            tour.start();
          }
        }, 500);
      }
    };

    initTour();

    return () => {
      // Cleanup on unmount or navigation
      if (tourRef.current && tourRef.current.isActive()) {
        tourRef.current.cancel();
      }
      hasStarted.current = false;
    };
  }, [tourName, steps, autoStart]);

  // Reset state when path changes
  useEffect(() => {
    currentPath.current = window.location.pathname;
    hasStarted.current = false;
    
    if (tourRef.current && tourRef.current.isActive()) {
      tourRef.current.cancel();
    }
  }, [window.location.pathname]);

  const startTour = async () => {
    if (hasRegionAccess() && validateTourForPage(tourName, window.location.pathname)) {
      const completed = await isTourCompleted(tourName);
      if (tourRef.current && !hasStarted.current && !completed) {
        hasStarted.current = true;
        tourRef.current.start();
      }
    }
  };

  return { startTour };
};
